import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage, Language } from '../contexts/LanguageContext';

// Google Analytics 4 — propiedad "Noor Passport Services" (creada 2026-10-23).
// Reglas de privacidad:
//  - NO se carga nada de Google hasta que el visitante pulsa "Aceptar".
//  - Solo se envía la ruta de la página (sin parámetros de la URL), para que ningún dato
//    personal pueda viajar en un enlace.
//  - IP anonimizada y sin señales de publicidad.
const GA_ID = 'G-38RL16XLBB';
const KEY = 'noor_analytics_consent';
type Choice = 'granted' | 'denied' | null;
function readChoice(): Choice {
  try {
    const v = window.localStorage.getItem(KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    return null;
  }
}
function saveChoice(v: 'granted' | 'denied') {
  try {
    window.localStorage.setItem(KEY, v);
  } catch {
    /* sin almacenamiento, el banner volverá a aparecer: no pasa nada */
  }
}
let gaLoaded = false;
function loadGA() {
  if (gaLoaded || typeof window === 'undefined') return;
  gaLoaded = true;
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  w.gtag = function () {
    w.dataLayer.push(arguments);
  };
  w.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied'
  });
  w.gtag('js', new Date());
  w.gtag('config', GA_ID, {
    send_page_view: false,
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false
  });
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
}
function sendPageView(pathname: string) {
  const w = window as any;
  if (!gaLoaded || !w.gtag) return;
  w.gtag('event', 'page_view', {
    page_path: pathname,
    page_location: window.location.origin + pathname,
    page_title: document.title
  });
}
const TEXT: Record<Language, {
  msg: string;
  more: string;
  yes: string;
  no: string;
  label: string;
}> = {
  es: {
    label: 'Aviso de analítica',
    msg: 'Usamos una analítica básica y anónima (Google Analytics) para saber qué páginas se visitan y mejorar el sitio. No usamos publicidad dirigida. ¿Nos permite medir las visitas?',
    more: 'Política de privacidad',
    yes: 'Aceptar',
    no: 'Rechazar'
  },
  en: {
    label: 'Analytics notice',
    msg: 'We use basic, anonymous analytics (Google Analytics) to see which pages are visited and improve the site. We do not use targeted advertising. May we measure visits?',
    more: 'Privacy policy',
    yes: 'Accept',
    no: 'Decline'
  },
  ht: {
    label: 'Avi analitik',
    msg: 'Nou itilize yon analitik senp e anonim (Google Analytics) pou konnen ki paj moun vizite ak amelyore sit la. Nou pa itilize piblisite sible. Èske nou ka konte vizit yo?',
    more: 'Politik konfidansyalite',
    yes: 'Aksepte',
    no: 'Refize'
  },
  vi: {
    label: 'Thông báo phân tích',
    msg: 'Chúng tôi dùng công cụ phân tích cơ bản, ẩn danh (Google Analytics) để biết trang nào được truy cập và cải thiện trang web. Chúng tôi không dùng quảng cáo nhắm mục tiêu. Quý vị có đồng ý cho chúng tôi đo lượt truy cập không?',
    more: 'Chính sách bảo mật',
    yes: 'Đồng ý',
    no: 'Từ chối'
  }
};
export function ConsentAnalytics() {
  const {
    pathname
  } = useLocation();
  const {
    language
  } = useLanguage();
  const [choice, setChoice] = useState<Choice>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const c = readChoice();
    setChoice(c);
    setReady(true);
    if (c === 'granted') loadGA();
  }, []);
  useEffect(() => {
    if (choice === 'granted') {
      loadGA();
      const key = pathname.length > 1 ? pathname.replace(/\/+$/, '') : '/';
      // El título de la página se actualiza en otro efecto; esperamos un instante.
      const t = window.setTimeout(() => sendPageView(key), 150);
      return () => window.clearTimeout(t);
    }
  }, [choice, pathname]);
  if (!ready || choice !== null) return null;
  const t = TEXT[language];
  const decide = (v: 'granted' | 'denied') => {
    saveChoice(v);
    setChoice(v);
  };
  return <div role="dialog" aria-label={t.label} className="fixed bottom-3 left-3 right-3 sm:right-auto sm:max-w-md z-[60] rounded-2xl border border-black/10 bg-white p-4 shadow-xl" data-id="element-78" data-mp="37sd148syyqg1">
      <p className="text-sm leading-relaxed text-brand-textPrimary" data-id="element-79" data-mp="3f7u83v6epx0">{t.msg}</p>
      <a href="/privacy-policy" className="mt-1 inline-block text-sm underline text-brand-textPrimary" data-id="element-80" data-mp="3vj0c52y6lw6t">
        {t.more}
      </a>
      <div className="mt-3 flex gap-2" data-id="element-81" data-mp="3qht20x8kgkwz">
        <button type="button" onClick={() => decide('granted')} className="flex-1 rounded-full bg-brand-textPrimary px-4 py-2 text-sm font-semibold text-white focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2" data-id="element-82" data-mp="3lkt6hg9cf0k4">
          {t.yes}
        </button>
        <button type="button" onClick={() => decide('denied')} className="flex-1 rounded-full border border-brand-textPrimary px-4 py-2 text-sm font-semibold text-brand-textPrimary focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2" data-id="element-83" data-mp="201z7twfyvu75">
          {t.no}
        </button>
      </div>
    </div>;
}